import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// "What you get" — benefits + photo proof promo (15s).
// Records at 1080x1920 via /app/scripts/record_promo.py --url .../promo3

const PHOTOS = {
  hero: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/5mpzbdp8_sweat_with_slay_7.png",
  about: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/nb2spi5w_IMG_8014%20%281%29.jpg",
  social: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/c7xf6eol_CEBC811F-CCAB-4A09-A66E-6D8B9203D7F0.jpeg",
};

const SAFE_TOP = 360;
const SAFE_BOTTOM = 580;

const BgPhoto = ({ src, kenBurns = "in" }) => (
  <motion.img
    src={src}
    alt=""
    initial={{ scale: kenBurns === "in" ? 1.0 : 1.12, opacity: 0 }}
    animate={{ scale: kenBurns === "in" ? 1.12 : 1.0, opacity: 1 }}
    transition={{ duration: 3.0, ease: "linear" }}
    className="absolute inset-0 w-full h-full object-cover"
  />
);

const SLIDES = [
  {
    id: "open",
    duration: 2200,
    bg: "#FF1B91",
    render: () => (
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-white/85 uppercase tracking-[0.5em] text-sm font-semibold mb-10">
          Sweat With Slay
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
          style={{ fontSize: "200px" }}
        >
          What<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "220px" }}>you</span><br />
          get.
        </motion.h1>
      </div>
    ),
  },
  {
    id: "programming",
    duration: 2400,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.hero} kenBurns="in" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end pb-[560px] px-16">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-base font-bold mb-5">
            01 / Programming
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "140px" }}>
            Custom plans.<br />
            <span className="text-[#FF1B91]">Not templates.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.55 }} className="mt-6 text-white/80 text-xl">
            Built around your goals, schedule, body & equipment.
          </motion.p>
        </div>
      </>
    ),
  },
  {
    id: "checkins",
    duration: 2400,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.about} kenBurns="out" />
        <div className="absolute inset-0 bg-gradient-to-l from-black/85 via-black/55 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end pb-[640px] px-16 items-end text-right">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-base font-bold mb-5">
            02 / Coaching
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "140px" }}>
            Form check<br />
            <span className="text-[#FF1B91]">on every lift.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.55 }} className="mt-6 text-white/80 text-xl max-w-md ml-auto">
            Upload a video — get voice notes back. Weekly check-ins. Real fixes.
          </motion.p>
        </div>
      </>
    ),
  },
  {
    id: "certs",
    duration: 2400,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-base font-bold mb-5">
          03 / Credentials
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-8" style={{ fontSize: "140px" }}>
          A coach with<br />
          <span className="text-[#FF1B91]">receipts.</span>
        </motion.h2>
        <div className="grid grid-cols-3 gap-3">
          {["NASM CPT", "NFPT CPT", "NASM CNC", "CPR / AED", "ERGO Next", "BBA Iona"].map((c, i) => (
            <motion.div
              key={c}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, delay: 0.35 + i * 0.08 }}
              className="border border-[#FF1B91]/40 bg-[#FF1B91]/5 py-4 text-center"
            >
              <p className="font-display font-black uppercase text-white text-xl tracking-tighter leading-none">{c}</p>
            </motion.div>
          ))}
        </div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 1.0 }} className="mt-6 text-white/55 text-lg text-center">
          Insured · Verifiable · NCCA-aligned
        </motion.p>
      </div>
    ),
  },
  {
    id: "everfit",
    duration: 2200,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.social} kenBurns="in" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-16 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-base font-bold mb-8">
            04 / In your pocket
          </motion.p>
          <motion.h2 initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "160px" }}>
            100% digital.<br />
            <span className="text-[#FF1B91]">Goes everywhere.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.55 }} className="mt-8 text-white/85 text-2xl">
            Delivered through the Everfit app.
          </motion.p>
        </div>
      </>
    ),
  },
  {
    id: "cta",
    duration: 3400,
    bg: "#FF1B91",
    render: () => (
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="text-white/85 uppercase tracking-[0.5em] text-sm font-bold mb-10">
          / 15 minutes to start
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
          style={{ fontSize: "200px" }}
        >
          Slide<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "220px" }}>into</span><br />
          my DMs.
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }} className="mt-10 text-white text-4xl font-bold tracking-tight">
          @sweatwithslay
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1.0 }} className="mt-3 text-white/85 uppercase tracking-[0.4em] text-base font-semibold">
          sweatwithslay.com
        </motion.p>
      </div>
    ),
  },
];

const TOTAL = SLIDES.reduce((acc, s) => acc + s.duration, 0);

export default function Promo3() {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const imgs = Array.from(document.images);
    if (imgs.length === 0) { setReady(true); return; }
    const pending = imgs.filter((i) => !(i.complete && i.naturalHeight > 0));
    if (pending.length === 0) { setReady(true); return; }
    let left = pending.length;
    const done = () => { left -= 1; if (left <= 0) setReady(true); };
    pending.forEach((i) => { i.addEventListener("load", done, { once: true }); i.addEventListener("error", done, { once: true }); });
    const fallback = setTimeout(() => setReady(true), 8000);
    return () => clearTimeout(fallback);
  }, []);

  useEffect(() => {
    if (!ready) return;
    document.body.setAttribute("data-promo-started", "true");
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      let total = 0;
      let active = 0;
      for (let i = 0; i < SLIDES.length; i++) {
        total += SLIDES[i].duration;
        if (elapsed < total) { active = i; break; }
        active = i;
      }
      setIdx(active);
      setProgress(Math.min(1, elapsed / TOTAL));
      if (elapsed < TOTAL) raf = requestAnimationFrame(tick);
      else document.body.setAttribute("data-promo-done", "true");
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  const slide = SLIDES[idx];

  return (
    <main
      data-testid="promo-page"
      data-promo-active={slide.id}
      className="font-body overflow-hidden fixed inset-0"
      style={{ background: slide.bg, color: "#fff" }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay z-20"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
      />

      <AnimatePresence>
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={slide.full ? "absolute inset-0" : "absolute left-0 right-0 px-16"}
          style={slide.full ? {} : { top: SAFE_TOP, bottom: SAFE_BOTTOM }}
        >
          {slide.render()}
        </motion.div>
      </AnimatePresence>

      <div className="absolute left-12 right-12 h-1 bg-white/15 z-30" style={{ top: SAFE_TOP - 30 }}>
        <div className="h-full bg-white" style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }} />
      </div>

      <div
        className="absolute left-0 right-0 text-center text-white/75 text-base uppercase tracking-[0.4em] font-semibold z-30"
        style={{ bottom: SAFE_BOTTOM - 60 }}
      >
        sweatwithslay.com
      </div>
    </main>
  );
}
